import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout/DashboardLayout";
import { adminNavItems } from "./_nav";
import { useLang } from "@/contexts/LanguageContext";
import api, { API } from "@/lib/api";
import { FileText, Download, Megaphone, Coins, Rocket } from "lucide-react";

const ICONS = { marketing: Megaphone, investor: Coins, launch: Rocket };
const ACCENTS = { marketing: "#FF6B00", investor: "#7E22CE", launch: "#10B981" };

export default function StrategyDocs() {
  const { t } = useLang();
  const [docs, setDocs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [content, setContent] = useState("");

  useEffect(() => { api.get("/docs/list").then(r => setDocs(r.data)); }, []);

  const openPreview = async (key) => {
    setPreview(key);
    const res = await fetch(`${API}/docs/download/${key}`);
    setContent(await res.text());
  };

  return (
    <DashboardLayout items={adminNavItems(t)} title="Strategy Docs">
      <div className="mb-6 max-w-2xl">
        <p className="text-[var(--so-text-soft)]">
          Investor-ready strategic documents. Download in Markdown for editing in any tool, or preview inline.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {docs.map((d) => {
          const Icon = ICONS[d.key] || FileText;
          const accent = ACCENTS[d.key] || "#FF6B00";
          return (
            <div key={d.key} className="so-card p-6" data-testid={`doc-card-${d.key}`}>
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${accent}15`, color: accent }}>
                  <Icon className="w-7 h-7" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-lg">{d.title}</div>
                  <div className="text-sm text-[var(--so-text-soft)] mt-1">{d.filename}</div>
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <button onClick={() => openPreview(d.key)} className="so-btn-secondary text-sm py-2 px-3" data-testid={`preview-${d.key}`}>
                      <FileText className="w-4 h-4" /> Preview
                    </button>
                    <a href={`${API}/docs/download/${d.key}`} download className="so-btn-primary text-sm py-2 px-3" data-testid={`download-${d.key}`}>
                      <Download className="w-4 h-4" /> Download
                    </a>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {preview && (
        <div className="so-card p-6" data-testid="doc-preview">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-lg">Preview · {docs.find(d => d.key === preview)?.title}</h2>
            <button onClick={() => setPreview(null)} className="text-sm text-[var(--so-text-soft)] hover:text-[var(--so-orange)]" data-testid="close-preview">Close</button>
          </div>
          <pre className="text-sm whitespace-pre-wrap font-mono bg-[var(--so-bg)] p-5 rounded-xl max-h-[600px] overflow-auto leading-relaxed">{content}</pre>
        </div>
      )}
    </DashboardLayout>
  );
}
